import { House, LogIn } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LinkAction } from "../components/Screen.js";
import { Link } from "react-router";

import { useSession } from "../hooks/useSession.js";
import {
  API_ERROR,
  HttpError,
  fetchArchive,
  type ArchiveLine,
} from "../services/http.js";

/**
 * What has been played here — `/archive`, phase 16j-3.
 *
 * A browsable history of the **installation** rather than of one account, which
 * is what was asked for: *"good gaming data to show"*.
 *
 * # ⚠️ Signed-in only, and unlike the hall of fame it could not be otherwise
 *
 * The hall of fame can restrict itself to account holders. An archive of games
 * has to name **everybody who played** or it is a record with holes in it — and
 * guests play without accounts (ADR 0007), so this publishes display names typed
 * by people who never signed up for a public profile.
 *
 * Signed-in-only is the conservative reading of what was authorised and the
 * recoverable direction: widening it later is a line, while a name that has
 * already been public cannot be un-published.
 *
 * # "No games yet" rather than an empty list
 *
 * Same decision as the hall of fame, and the same reason: **no invented games**
 * to make the installation look busy. An honest empty state reads as *new*; a
 * blank list reads as *broken*.
 */
export function ArchivePage() {
  const { t } = useTranslation();
  const { account } = useSession();
  const [games, setGames] = useState<ArchiveLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!account) {
      return;
    }
    let cancelled = false;
    void fetchArchive().then(
      (loaded) => {
        if (!cancelled) {
          setGames(loaded);
        }
      },
      (cause: unknown) => {
        if (cancelled) {
          return;
        }
        if (cause instanceof HttpError && cause.code === API_ERROR.unauthenticated) {
          return;
        }
        setError(cause instanceof Error ? cause.message : t("career.loadFailed"));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [account, t]);

  if (account === undefined) {
    return (
      <Frame>
        <p className="text-sm text-white/50">{t("archive.loading")}</p>
      </Frame>
    );
  }

  if (account === null) {
    return (
      <Frame>
        <p className="text-sm text-white/60">{t("archive.needsAccount")}</p>
        <LinkAction to="/login" icon={LogIn} accent>
          {t("login.title")}
        </LinkAction>
      </Frame>
    );
  }

  if (error) {
    return (
      <Frame>
        <p role="alert" className="text-sm text-wrong-500">
          {error}
        </p>
      </Frame>
    );
  }

  if (!games) {
    return (
      <Frame>
        <p className="text-sm text-white/50">{t("archive.loading")}</p>
      </Frame>
    );
  }

  if (games.length === 0) {
    return (
      <Frame>
        <h2 className="text-xl font-semibold">{t("archive.emptyTitle")}</h2>
        <p className="mt-1 text-sm text-white/60">{t("archive.emptyLead")}</p>
        <LinkAction to="/" icon={House} accent>
          {t("hall.startOne")}
        </LinkAction>
      </Frame>
    );
  }

  return (
    <Frame>
      <ol className="flex flex-col gap-1" data-testid="archive-list">
        {games.map((game) => (
          <li key={game.game_id}>
            <Link
              to={`/recap/${game.game_id}`}
              className="flex flex-col gap-0.5 rounded-lg px-2 py-2 hover:bg-white/5"
            >
              <span className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-sm">
                  {game.party_name ?? t("archive.unnamed")}
                  <span className="ml-2 text-xs uppercase tracking-wide text-white/40">
                    {game.mode}
                  </span>
                </span>
                <span className="shrink-0 text-xs tabular-nums text-white/40">
                  {t("archive.rounds", { count: game.rounds })}
                </span>
              </span>
              {game.players.length > 0 && (
                <span className="truncate text-xs text-white/50">
                  {game.players.join(" · ")}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ol>
    </Frame>
  );
}

/** One frame for every state, so the header does not move as the page resolves. */
function Frame({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();

  return (
    <main className="mx-auto flex min-h-full w-full max-w-3xl flex-col p-5 pb-16">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-flash-500">
          {t("app.name")}
        </p>
        <h1 className="mt-1 text-3xl font-semibold">{t("archive.title")}</h1>
        <p className="mt-1 text-sm text-white/60">{t("archive.lead")}</p>
      </header>
      {children}
    </main>
  );
}
